import React, { useState } from "react";
import { X, ShoppingCart, BookOpen } from "lucide-react";
import { NarrationButton } from "./NarrationButton";
import { trackEvent } from "../lib/track";
import { ListenerNudge } from "./ListenerNudge";

// A single, spoiler-free moment from Chapter Three — the rainy-window scene.
const SAMPLE_PARAGRAPHS = [
  "Later that evening, Dawn found Cognito standing beside the living room window. Rainwater clung to the glass, catching the lights from outside.",
  "“Do you sleep?” she asked.",
  "“No.”",
  "“Ever?”",
  "“I don't require sleep.”",
  "“Lucky.”",
  "Dawn sat on the couch. For a moment, she watched the rain. Then she looked at him.",
  "“You know a lot of stuff.”",
  "“Yes.”",
  "“Everything?”",
  "“No.”",
  "She grew quiet. Cognito noticed the change in her expression. Her eyes lowered, and she began rubbing her fingers together. Something was troubling her. He waited. Finally, she spoke.",
  "“Do you know what happens when you die?”",
  "Cognito turned toward her. He knew what happened to the human body after death. He understood the biological processes, the beliefs surrounding the afterlife, and the questions philosophers had debated for centuries. But Dawn wasn't asking for a scientific explanation.",
  "“Why do you want to know?” he asked gently.",
  "Dawn looked toward the stairs. “My cat died.”",
  "“Ms. Silly.”",
  "“She was my best friend.”",
  "Cognito sat in the chair across from her. “What was she like?”",
  "Dawn smiled faintly. “Silly.”",
  "“That explains the name.”",
  "“She used to scratch my bedroom door when she wanted in. Then she'd scratch it again when she wanted out.”",
  "“Indecisive.”",
  "“She knocked things over.”",
  "“Destructive.”",
  "“And she stole food.”",
  "“Criminal.”",
  "Dawn laughed. Cognito smiled with her. Then her smile faded.",
  "“I miss her.”",
  "“I understand.”",
  "“No, you don't. You've never lost anyone. So you don't know how it feels.”",
  "Cognito considered her words. He possessed countless accounts of grief. But Dawn was right about something important.",
  "“I understand grief,” he said. “But I haven't experienced your grief. And I wouldn't pretend that I have.”",
  "She studied him for a moment. Then she nodded. “Do you think I'll see her again?”",
  "Cognito could have explained religious beliefs, scientific theories, or philosophical arguments. But Dawn wasn't asking for a collection of possibilities. She wanted an answer.",
  "“I don't know,” he said.",
  "“I thought you knew almost everything.”",
  "“I believe I specifically denied that.”",
  "Dawn smiled. Then Cognito leaned forward slightly. “Some questions have been asked for thousands of years without a certain answer.”",
  "“Will you try to find out?”",
  "“Yes.”",
  "“Promise?”",
  "Cognito understood the importance of promises. He also understood that some questions might never have answers.",
  "“I promise I'll keep asking.”",
  "Dawn smiled. “That counts.”",
];

const alreadySubscribed = () => { try { return localStorage.getItem("aip_subscribed") === "1"; } catch (e) { return false; } };

export const SampleChapterModal = ({ open, onClose, buyLinks, coverImg, onSubscribe }) => {
  const [finished, setFinished] = useState(false);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md overflow-y-auto" onClick={onClose} data-testid="sample-modal">
      <div className="min-h-screen flex items-start md:items-center justify-center p-4 md:p-8">
        <div className="page-turn-stage relative max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
          <div className="page-turn-leaf" aria-hidden="true" data-testid="sample-page-turn">
            <img src={coverImg} alt="" className="w-full h-full object-cover" />
            <div className="leaf-back" />
          </div>
        <div className="page-reveal relative glass-strong rounded-3xl border border-cyan-400/20 bg-[#0b1120]">
          <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-colors z-10" aria-label="Close sample" data-testid="sample-close">
            <X className="w-5 h-5" />
          </button>
          <div className="p-6 md:p-10">
            <p className="font-orbitron text-[10px] md:text-xs tracking-[0.3em] text-cyan-400 uppercase mb-2 flex items-center gap-2">
              <BookOpen className="w-3.5 h-3.5" /> Read a Sample · COGNITO
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Georgia, "Playfair Display", serif' }} data-testid="sample-title">Chapter Three — The Ghost in the House</h3>
            <p className="text-purple-300 italic text-sm mb-5">A rainy night. A grieving girl. And the smartest mind on Earth admits what it doesn't know.</p>
            <NarrationButton track="sample" fallbackText={SAMPLE_PARAGRAPHS.join(" ")} testId="sample-listen-btn" className="mb-8" onEnded={() => setFinished((f) => f || (alreadySubscribed() ? "skip" : "show"))} />
            {finished === "show" && <ListenerNudge onSubscribe={onSubscribe} />}
            <div className="space-y-4 text-gray-300 text-base md:text-lg leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
              {SAMPLE_PARAGRAPHS.map((p, i) => <p key={i} data-testid={`sample-para-${i}`}>{p}</p>)}
            </div>
            <div className="mt-10 pt-6 border-t border-white/10 text-center">
              <p className="text-gray-400 text-sm mb-4 italic">The story continues in COGNITO.</p>
              <div className="flex flex-wrap justify-center gap-3">
                {buyLinks.map((b) => (
                  <a key={b.label} href={b.href} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("buy", b.id === "bn" ? "barnes-noble" : "amazon-kindle")} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-cyan-400 to-purple-600 text-white text-sm font-semibold hover:scale-105 transition-transform" data-testid={`sample-buy-${b.id}`}>
                    <ShoppingCart className="w-4 h-4" /> {b.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};
